import React, { useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";

const PayPalModal = ({
  isOpen,
  onClose,
  amount,
  transactionId,
  donorEmail,
  onSuccess,
  onError,
}) => {
  const modalRef = useRef(null);
  const paypalButtonsRef = useRef(null);
  const scriptLoadedRef = useRef(false);

  // Load PayPal SDK dynamically
  useEffect(() => {
    if (!isOpen) return;

    const loadPayPalScript = () => {
      if (scriptLoadedRef.current) return;

      const script = document.createElement("script");
      // Use sandbox client ID for testing
      script.src =
        "https://www.paypal.com/sdk/js?client-id=AX31NtGJHt1_QuDVpIJ9kxMbyvPdJSnYtT9YFEmaPtNwqMWfqtwCaFxp5HE8z9XwiFoVCTCeQNBO4Gw_&currency=AUD"; // For production, replace with your actual client ID:
      // script.src = 'https://www.paypal.com/sdk/js?client-id=YOUR_LIVE_CLIENT_ID&currency=AUD';
      //   AX31NtGJHt1_QuDVpIJ9kxMbyvPdJSnYtT9YFEmaPtNwqMWfqtwCaFxp5HE8z9XwiFoVCTCeQNBO4Gw_

      script.addEventListener("load", () => {
        scriptLoadedRef.current = true;
        renderPayPalButtons();
      });

      script.addEventListener("error", () => {
        onError("Failed to load PayPal. Please try again.");
      });

      document.body.appendChild(script);
    };

    const renderPayPalButtons = () => {
      if (!window.paypal || !paypalButtonsRef.current) return;

      // Clear any existing buttons
      paypalButtonsRef.current.innerHTML = "";

      // Render the PayPal buttons
      window.paypal
        .Buttons({
          // Set up the transaction
          createOrder: (data, actions) => {
            return actions.order.create({
              purchase_units: [
                {
                  amount: {
                    value: amount.toString(),
                    currency_code: "AUD",
                  },
                  description: `Donation Transaction: ${transactionId}`,
                  custom_id: transactionId,
                },
              ],
              application_context: {
                shipping_preference: "NO_SHIPPING",
                brand_name: "Sydney Sri Ayyappa Swami Centre",
                landing_page: "BILLING",
                user_action: "PAY_NOW",
              },
            });
          },
          // Finalize the transaction
          onApprove: (data, actions) => {
            return actions.order.capture().then((details) => {
              // Payment successful
              onSuccess({
                transactionId,
                paypalOrderId: data.orderID,
                paypalPayerId: details.payer.email_address,
                amount: amount,
                status: "completed",
                details: details,
              });
            });
          },
          // Handle errors
          onError: (err) => {
            console.error("PayPal Error:", err);
            onError("Payment failed. Please try again.");
          },
          // Handle cancellation
          onCancel: (data) => {
            onError("Payment was cancelled.");
          },
          style: {
            layout: "vertical",
            color: "gold",
            shape: "rect",
            label: "paypal",
            height: 40,
          },
        })
        .render(paypalButtonsRef.current);
    };

    if (isOpen) {
      loadPayPalScript();
    }

    // Cleanup
    return () => {
      // Clean up script if needed
    };
  }, [isOpen, amount, transactionId, onSuccess, onError]);

  // Close modal when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        onClose();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === "Escape") {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      document.addEventListener("keydown", handleEscapeKey);
      // Prevent scrolling on body
      document.body.style.overflow = "hidden";
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("keydown", handleEscapeKey);
      document.body.style.overflow = "unset";
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="modal-backdrop fade show"
        style={{
          position: "fixed",
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: "rgba(0,0,0,0.5)",
          zIndex: 1050,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <motion.div
          ref={modalRef}
          initial={{ scale: 0.9, y: 20 }}
          animate={{ scale: 1, y: 0 }}
          exit={{ scale: 0.9, y: 20 }}
          className="modal-dialog modal-dialog-centered"
          style={{
            maxWidth: "500px",
            width: "100%",
            margin: "1.75rem auto",
          }}
        >
          <div
            className="modal-content border-0 rounded-4 shadow-lg p-3"
            style={{ backgroundColor: "white" }}
          >
            <div
              className="modal-header border-0 pb-0"
              style={{
                backgroundColor: "white",
                borderTopLeftRadius: "1rem",
                borderTopRightRadius: "1rem",
              }}
            >
              <h5 className="modal-title fw-bold">
                <i className="bi bi-paypal text-primary me-2"></i>
                Complete Your Payment
              </h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
                aria-label="Close"
              />
            </div>

            <div
              className="modal-body p-4"
              style={{ backgroundColor: "white" }}
            >
              <div className="text-center mb-4">
                <h3 className="fw-bold text-success mb-1">
                  ${parseFloat(amount).toFixed(2)} AUD
                </h3>
                <p className="text-muted small mb-0">
                  Transaction ID: {transactionId}
                </p>
                {donorEmail && (
                  <p className="text-muted small">
                    <i className="bi bi-envelope me-1"></i>
                    {donorEmail}
                  </p>
                )}
              </div>

              {/* PayPal Buttons Container */}
              <div
                ref={paypalButtonsRef}
                className="paypal-buttons-container bg-white p-3 rounded-3"
                style={{ minHeight: "150px" }}
              />

              <div className="text-center mt-3">
                <p className="text-muted small mb-0">
                  <i className="bi bi-shield-check text-success me-1"></i>
                  Secure payment processed by PayPal
                </p>
                <p className="text-muted small">
                  You'll be redirected to PayPal to complete your payment
                </p>
              </div>
            </div>

            <div
              className="modal-footer border-0 pt-0"
              style={{
                backgroundColor: "white",
                borderBottomLeftRadius: "1rem",
                borderBottomRightRadius: "1rem",
              }}
            >
              <button
                type="button"
                className="btn btn-light w-100 py-2 border rounded-pill"
                onClick={onClose}
              >
                Cancel
              </button>
            </div>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default PayPalModal;
